import type * as React from "react";
import { FileText, Pencil } from "lucide-react";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import { fileTypeLabel } from "./fileTypeLabel";
import { offeredSlotTypes, toTypeSlug, type TSubmissionContext } from "./submissionContext";
import type { TFileReviewDetails } from "./types";

export type TFileReviewTabId =
    | "review"
    | "etl"
    | "generated-gl"
    | "operating-statement"
    | "original"
    | "history"
    | "supporting";

/**
 * Every view the page can show, with the name it is referred to by.
 *
 * The Review view is offered once per file type under its group's own label, so
 * its entry here only names the view; every other label is also the radio's text
 * in the Read-only files group. This is the source of the valid `?tab=` values.
 */
export const FILE_REVIEW_TABS: { id: TFileReviewTabId; label: string }[] = [
    { id: "review", label: "Review" },
    { id: "etl", label: "ETL" },
    { id: "generated-gl", label: "Generated GL" },
    { id: "operating-statement", label: "Operating statement" },
    { id: "original", label: "Original files" },
    { id: "history", label: "Submission history" },
    { id: "supporting", label: "Supporting files" },
];

const TAB_IDS = new Set<string>(FILE_REVIEW_TABS.map((tab) => tab.id));

/** Anything unrecognised — including a hand-typed or stale `?tab=` value, or `null` — falls back to "review". */
export function parseTabId(raw: string | null): TFileReviewTabId {
    if (raw !== null && TAB_IDS.has(raw)) {
        return raw as TFileReviewTabId;
    }
    return "review";
}

const TAB_LABELS = Object.fromEntries(FILE_REVIEW_TABS.map((tab) => [tab.id, tab.label])) as Record<
    TFileReviewTabId,
    string
>;

/**
 * GL is generated only from a TB-only deal's trial balance, so only those files
 * have a Generated GL view to offer. An opening trial balance never generates GL.
 */
function offersGeneratedGL(file: TFileReviewDetails | null): file is TFileReviewDetails {
    return (
        file !== null && file.dealFileType === "tb_only" && file.fileType !== "open_trial_balance"
    );
}

/**
 * An operating statement is the income-statement view of a trial balance, so only
 * a processed TB has one — not an OTB (opening balances), a GL or a FinJournal.
 * Behind the `operating_statement_enabled` company flag while it rolls out.
 */
function offersOperatingStatement(
    file: TFileReviewDetails | null,
    enabled: boolean,
): file is TFileReviewDetails {
    return enabled && file !== null && file.fileType === "trial_balance";
}

type TNavOption = {
    key: string;
    label: string;
    live: boolean;
    checked: boolean;
    onChange: () => void;
    testId: string | undefined;
};

export type TFileReviewTabsProps = {
    /** `null` for a restricted caller, who never loads a file. */
    file: TFileReviewDetails | null;
    context: TAsyncState<TSubmissionContext>;
    activeTab: TFileReviewTabId;
    onSelectTab: (tab: TFileReviewTabId) => void;
    /** A supporting-files-only caller: exactly one view is theirs to see. */
    restricted?: boolean;
    /** The backend `FileType` wire value currently being reviewed — the selected type, not necessarily the open file's. */
    selectedFileType: string;
    /** A file type picked from the Review & Edit group, as a backend `FileType` wire value. */
    onSelectFileType: (fileType: string) => void;
    /** Review caps for TB/GL/OTB types; a type the user cannot review renders disabled rather than ejecting them from the page. */
    tbglCaps: { canReview: boolean };
    /** Review caps for FinJournal. See `tbglCaps`. */
    finjournalCaps: { canReview: boolean };
    /** The company's `operating_statement_enabled` flag. Off hides the option entirely. */
    operatingStatementEnabled?: boolean;
};

/**
 * The submission's navigation, in two bordered groups: Review & Edit, holding one
 * radio per file type the submission expects, and Read-only files, holding every
 * view that only shows the submission.
 *
 * The per-type radios are live by review capability, so a user can switch to a
 * sibling type's review without leaving the page. All radios share one name, so
 * exactly one view is checked across both groups.
 */
export function FileReviewTabs(props: TFileReviewTabsProps): React.ReactElement {
    const {
        file,
        context,
        activeTab,
        onSelectTab,
        restricted = false,
        selectedFileType,
        onSelectFileType,
        tbglCaps,
        finjournalCaps,
        operatingStatementEnabled = false,
    } = props;

    const data = context.status === "rejected" ? null : context.data;
    const offeredTypes = offeredSlotTypes(data);
    // The selection always gets an option to land on, because `checked` strictly
    // follows `selectedFileType` and never the anchor — leave it unrepresented and the
    // group renders with nothing checked. Two ways it can be missing from the slots:
    // before the context arrives there are no slots at all (a cold mount of a `?type=`
    // link), and afterwards an OTB or FJ selection can outlive its slot, which exists
    // only in months where such a file does. Appending keeps the backend's display
    // order intact — only OTB and FJ can vanish, and both already sort last.
    const perTypeOptions: string[] = [
        ...new Set(
            [
                ...(offeredTypes.length > 0 ? offeredTypes : file !== null ? [file.fileType] : []),
                selectedFileType,
            ].filter((type) => type !== ""),
        ),
    ];
    // An absent or still-loading context must grey the ETL option, not default it
    // live — there is nothing yet to say a generated export exists.
    const etlAvailable = data !== null && data.etlArtifacts.some((artifact) => artifact.available);

    // `checked` must mean *this exact view is showing* — nothing weaker. Checking a
    // radio for a view that is not showing makes it unreachable, because clicking an
    // already-checked radio fires no change event. A greyed option is never checked.
    const viewOption = (tab: TFileReviewTabId, live: boolean): TNavOption => ({
        key: tab,
        label: TAB_LABELS[tab],
        live,
        checked: live && activeTab === tab,
        onChange: () => onSelectTab(tab),
        testId: live ? `file-review-tab-${tab}` : undefined,
    });

    const reviewOptions: TNavOption[] = perTypeOptions.map((fileType) => {
        // Null-guarded for the restricted caller, who has no open file.
        const isOpenFileType = file !== null && fileType === file.fileType;
        const canReviewType =
            fileType === "financial_journals" ? finjournalCaps.canReview : tbglCaps.canReview;
        return {
            key: fileType,
            label: fileTypeLabel(fileType),
            // The open file's own type is always live: the user is already reviewing
            // it, so the shell has necessarily let them through, and disabling it would
            // strand them with no checked radio in the group. Every other type is gated
            // on whether this user may review that type at all.
            live: isOpenFileType || canReviewType,
            // The selected *type*, never the open file's — during a month with no file
            // of the selected type the anchor file is a different type, and checking
            // the anchor's radio there would contradict the empty state below it.
            checked: activeTab === "review" && fileType === selectedFileType,
            onChange: () => {
                onSelectFileType(fileType);
                onSelectTab("review");
            },
            // The open file's option keeps the group's entrance testid, so tab-selection
            // tests address the review view the same way as every other; sibling types
            // get their own, individually addressable one.
            testId: isOpenFileType
                ? "file-review-tab-review"
                : `file-review-type-${toTypeSlug(fileType) ?? fileType}`,
        };
    });

    // A restricted caller has exactly one view, so the others are not rendered
    // disabled — they are not theirs to see at all.
    const readonlyOptions: TNavOption[] = restricted
        ? [viewOption("supporting", true)]
        : [
              viewOption("etl", etlAvailable),
              // Greyed rather than hidden until GL is generated, so the group does not
              // change shape under the user when the Generate GL run completes.
              ...(offersGeneratedGL(file)
                  ? [viewOption("generated-gl", file.hasGLGeneratedLines)]
                  : []),
              ...(offersOperatingStatement(file, operatingStatementEnabled)
                  ? [viewOption("operating-statement", file.totalLineItems > 0)]
                  : []),
              viewOption("original", true),
              viewOption("history", true),
              viewOption("supporting", true),
          ];

    const groups: { id: string; label: string; icon: typeof Pencil; options: TNavOption[] }[] = [
        ...(restricted
            ? []
            : [{ id: "review", label: "Review & Edit", icon: Pencil, options: reviewOptions }]),
        { id: "readonly", label: "Read-only files", icon: FileText, options: readonlyOptions },
    ];

    return (
        <div className="flex flex-wrap items-stretch gap-3">
            {groups.map((group) => {
                const isHighlighted =
                    group.id === "review" ? activeTab === "review" : activeTab !== "review";

                return (
                    <fieldset
                        key={group.id}
                        data-testid={`file-review-group-${group.id}`}
                        className={cn(
                            "rounded-lg border px-3 pb-2.5 pt-1 transition-colors",
                            isHighlighted ? "border-primary" : "border-border",
                        )}
                    >
                        <legend
                            className={cn(
                                "flex items-center gap-1.5 px-1 text-xs font-medium",
                                isHighlighted ? "text-primary" : "text-muted-foreground",
                            )}
                        >
                            <group.icon className="h-3.5 w-3.5" aria-hidden />
                            {group.label}
                        </legend>

                        <div className="flex items-center gap-4">
                            {group.options.map((option) => (
                                <label
                                    key={option.key}
                                    className={cn(
                                        "flex items-center gap-2 text-sm",
                                        option.live
                                            ? "cursor-pointer"
                                            : "cursor-not-allowed opacity-50",
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="file-review-view"
                                        className="h-4 w-4 accent-primary"
                                        checked={option.checked}
                                        disabled={!option.live}
                                        onChange={option.onChange}
                                        {...(option.testId !== undefined
                                            ? { "data-testid": option.testId }
                                            : {})}
                                    />
                                    <span
                                        className={cn(
                                            option.checked && "font-medium text-foreground",
                                        )}
                                    >
                                        {option.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                );
            })}
        </div>
    );
}

// Derived from the view names so a rename cannot leave the placeholder copy behind.
// "review" never reaches this component in practice — the shell only renders it for
// the other views — but the shared `TFileReviewTabId` keeps the prop type honest.
const PLACEHOLDER_COPY: Record<TFileReviewTabId, string> = Object.fromEntries(
    FILE_REVIEW_TABS.map((tab) => [tab.id, `${tab.label} — coming soon.`]),
) as Record<TFileReviewTabId, string>;

export type TFileReviewPlaceholderProps = { tab: TFileReviewTabId };

export function FileReviewTabPlaceholder(props: TFileReviewPlaceholderProps): React.ReactElement {
    const { tab } = props;

    return (
        <div data-testid={`file-review-tab-placeholder-${tab}`}>
            <EmptyState title={PLACEHOLDER_COPY[tab]} />
        </div>
    );
}
